import { faEye } from '@fortawesome/free-solid-svg-icons/faEye';
import { updatedDiff } from 'deep-object-diff';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { AtiraSelect } from '../../../components/AtiraSelect';
import { Button } from '../../../components/Button';
import { Flex } from '../../../components/Flex';
import { Text } from '../../../components/Text';
import { Textarea } from '../../../components/Textarea';
import { Modal } from '../../../components/modal/Modal';
import { EditThemeDto } from '../../../model/admin/dto/EditThemeDto';
import { AtiraLinkThemes } from '../../../model/atira-link/types/AtiraLinkThemes.enum';
import { AtiraLinkPlanType } from '../../../model/plan/types/AtiraLinkPlanType.enum';
import { Lengths } from '../../../model/shared/enum/Lengths.enum';
import { AtiraLinkTheme } from '../../../model/theme/AtiraLinkTheme';
import { AtiraLinkPlanThemeTier } from '../../../model/theme/type/AtiraLinkPlanThemeTier';
import { AtiraLinkThemeStatus } from '../../../model/theme/type/AtiraLinkThemeStatus.enum';
import { adminSliceSelectors } from '../../../redux/admin/admin.selector';
import { adminActions } from '../../../redux/admin/admin.slice';
import { paymentActions } from '../../../redux/payment/payment.slice';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { userSliceSelectors } from '../../../redux/user/user.selector';
import { Spacing } from '../../../theme/Spacing';
import { AtiraToast } from '../../../utils/AtiraToast';
import { Tooltip } from '../../../utils/local-antd';
import { onSetTypedCRUDErrors } from '../../../utils/onSetTypedCRUDErrors';
import { getPlanTitle } from '../../../utils/pricingUtils';
import { TemplatePreviewModal } from '../../shared/components/TemplatePreviewModal';

const Wrapper = styled(Flex)`
  width: 100%;
  flex-direction: column;
  gap: ${Spacing.m};
`;

const StyledButton = styled(Button)`
  margin: 0;
  margin-top: ${Spacing.m};
  height: 2.3rem;
  width: 100%;

  &:hover {
    background-color: ${({ theme }) => theme.lightMain};
  }
`;

const PreviewButton = styled(Button)`
  margin: 0;
  margin-top: auto;
  padding: ${Spacing.s};
  height: 2.3rem;
`;

const BoldSpan = styled.span`
  font-weight: bold;
`;

const StyledTextarea = styled(Textarea)`
  border-color: ${({ theme, valid }) =>
    theme[valid ? 'textColor' : 'danger']} !important;
`;

const StyledSelect = styled(AtiraSelect)`
  .ant-select-selector {
    border-color: ${({ theme, valid }) =>
      theme[valid ? 'textColor' : 'danger']} !important;
  }
`;

type AdminThemeManageModalProps = {
  open: boolean;
  onClose: VoidFunction;
  theme?: AtiraLinkTheme | null;
};

export const AdminThemeManageModal: React.FC<AdminThemeManageModalProps> = ({
  open = false,
  onClose,
  theme,
}) => {
  const themes = useAppSelector(adminSliceSelectors.selectAllThemes);

  const [loading, setLoading] = useState(false);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);

  const { t } = useTranslation();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const themesPage = useAppSelector(adminSliceSelectors.selectThemesPage);

  const { control, handleSubmit, getValues, reset, setError } =
    useForm<EditThemeDto>();

  const dispatch = useAppDispatch();

  const onReset = (newValues: Record<string, any>) => {
    reset(newValues);
  };

  const onEdit = async () => {
    try {
      setLoading(true);

      const dto = getValues();

      const selectedTheme = themes.find((theme) => theme._id === dto.themeId);

      const payload = updatedDiff(
        {
          themeId: selectedTheme?._id,
          name: selectedTheme?.name,
          description: selectedTheme?.description,
          userId: selectedTheme?.userId,
          status: selectedTheme?.status,
          tier: selectedTheme?.tier,
        },
        dto,
      ) as EditThemeDto;

      if (Object.keys(payload).length) {
        payload.userId = userId;
        payload.themeId = selectedTheme?._id!;

        await dispatch(adminActions.editTheme(payload)).unwrap();
        await dispatch(
          adminActions.getThemes({
            meta: {
              page: themesPage,
              count: 20,
            },
          }),
        ).unwrap();
      }

      AtiraToast.success(t('admin.themes.manage.success'));

      onReset({
        themeId: '',
        name: '',
        description: '',
        status: undefined,
        tier: undefined,
        userId: '',
      });
      onClose();
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
      onSetTypedCRUDErrors(e, setError);
    } finally {
      setLoading(false);
    }
  };

  const onLocalClose = () => {
    onReset({
      name: '',
      description: '',
      status: undefined,
      tier: undefined,
      userId: '',
      themeId: '',
    });
    onClose();
  };

  useEffect(() => {
    if (theme && open) {
      reset({
        name: theme.name,
        description: theme.description,
        status: theme.status,
        userId,
        themeId: theme._id,
        tier: theme.tier,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme, open]);

  useEffect(() => {
    dispatch(paymentActions.getAllPlans());
  }, [dispatch]);

  if (!theme) {
    return null;
  }

  return (
    <Modal
      open={open}
      onClose={onLocalClose}
      title={t('admin.themes.manage.modal.title')}
    >
      <Wrapper>
        <Flex gap="s" alignItems="center" justifyContent="space-between">
          <Text>
            <BoldSpan>{t('common.theme')}: </BoldSpan>
            {theme.name}
          </Text>

          <Tooltip title={t('admin.themes.preview_theme')}>
            <PreviewButton
              onClick={() => setPreviewModalVisible(true)}
              icon={faEye}
            />
          </Tooltip>
        </Flex>

        <Controller
          control={control}
          name="tier"
          rules={{
            required: {
              value: true,
              message: t('common.this_field_required'),
            },
          }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <StyledSelect
              value={value}
              onChange={onChange}
              options={Object.entries(AtiraLinkPlanThemeTier).map(
                ([key, tier]) => ({
                  label: t(getPlanTitle(key as AtiraLinkPlanType)),
                  value: tier,
                }),
              )}
              valid={!error}
              errorMessage={error?.message}
              title={t('admin.themes.manage.modal.availableTo.title')}
              info={t('admin.themes.manage.modal.availableTo.info')}
              required
              containerStyle={{ flexGrow: 1 }}
              style={{ height: '2.3rem' }}
            />
          )}
        />

        <Controller
          control={control}
          name="status"
          rules={{
            required: {
              value: true,
              message: t('common.this_field_required'),
            },
          }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <StyledSelect
              value={value || null}
              onChange={onChange}
              options={Object.keys(AtiraLinkThemeStatus).map((key) => ({
                label: t(`admin.theme.status.title-${key.toLowerCase()}`),
                value: key,
              }))}
              valid={!error}
              errorMessage={error?.message}
              title={t('common.status')}
              required
              containerStyle={{ flexGrow: 1 }}
              style={{ height: '2.3rem' }}
            />
          )}
        />

        <Controller
          control={control}
          name="description"
          rules={{
            maxLength: {
              value: Lengths.NAME,
              message: t('error.length.less', {
                length: Lengths.DESCRIPTION,
                name: t('common.description'),
              }),
            },
          }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <StyledTextarea
              value={value}
              onChange={onChange}
              valid={!error}
              errorMessage={error?.message}
              title={t('common.description')}
            />
          )}
        />

        <StyledButton loading={loading} onClick={handleSubmit(onEdit)}>
          {t('common.edit')}
        </StyledButton>
      </Wrapper>

      <TemplatePreviewModal
        selectedTheme={
          AtiraLinkThemes[theme.name as keyof typeof AtiraLinkThemes]
        }
        open={previewModalVisible}
        onClose={() => setPreviewModalVisible(false)}
      />
    </Modal>
  );
};
